// Hotel context management utilities

const HOTEL_CODE_KEY = 'hotel_code';

/**
 * Extract hotel code from URL query parameter
 */
export const extractHotelCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('h');
};

/**
 * Get hotel code from localStorage
 */
export const getStoredHotelCode = (): string | null => {
  try {
    return localStorage.getItem(HOTEL_CODE_KEY);
  } catch (error) {
    console.warn('Failed to read hotel code from localStorage:', error);
    return null;
  }
};

/**
 * Store hotel code in localStorage
 */
export const storeHotelCode = (hotelCode: string): void => {
  try {
    localStorage.setItem(HOTEL_CODE_KEY, hotelCode);
    console.log('Hotel code stored:', hotelCode);
  } catch (error) {
    console.warn('Failed to store hotel code in localStorage:', error);
  }
};

/**
 * Remove hotel code from localStorage
 */
export const clearHotelCode = (): void => {
  try {
    localStorage.removeItem(HOTEL_CODE_KEY);
    console.log('Hotel code cleared from localStorage');
  } catch (error) {
    console.warn('Failed to clear hotel code from localStorage:', error);
  }
};

/**
 * Get current hotel code - checks URL first, then localStorage
 */
export const getCurrentHotelCode = (): string | null => {
  // First try to get from URL
  const urlHotelCode = extractHotelCodeFromUrl();
  if (urlHotelCode) {
    // Store it for future use
    storeHotelCode(urlHotelCode);
    markQrEntry(urlHotelCode);
    return urlHotelCode;
  }
  
  // Fallback to localStorage
  return getStoredHotelCode();
};

/**
 * Initialize hotel context on app start
 */
export const initializeHotelContext = (): string | null => {
  const hotelCode = getCurrentHotelCode();
  
  if (hotelCode) {
    console.log('Hotel context initialized:', hotelCode);
  } else {
    console.log('No hotel code found in URL or localStorage');
  }
  
  return hotelCode;
};
// Every hotel QR code (room / reception / kiosk) opens the app with
// ?h=<HOTEL_CODE>; links in emails and the stored hotel code after a
// login never do. Remember the QR arrival for this browser session so
// QR-only options (the guest-name login fallback) survive the hop from
// /hotel-info to /login, but never leak into a later non-QR visit.
const QR_ENTRY_KEY = 'gd_qr_entry_hotel';

export const markQrEntry = (hotelCode: string): void => {
  try {
    sessionStorage.setItem(QR_ENTRY_KEY, hotelCode.toUpperCase());
  } catch {
    // private mode / storage blocked — the fallback just won't show
  }
};

/** The hotel code of the QR this session was opened from, or null. */
export const getQrEntryHotelCode = (): string | null => {
  try {
    return sessionStorage.getItem(QR_ENTRY_KEY);
  } catch {
    return null;
  }
};
